import { useEffect, useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { AionLogoArtwork } from "@/components/aion/AionLogoArtwork";
import { bootstrapPlatformAdmin, establishBootstrapAdminSession, getBootstrapAdminStatus, getRegistrationStatus } from "@/services/api";

const readBootstrapToken = () => {
  const hash = new URLSearchParams(String(window.location.hash || "").replace(/^#/, ""));
  const query = new URLSearchParams(window.location.search);
  return hash.get("token") || query.get("token") || "";
};

export const BootstrapAdminScreen = ({ onAuthenticated }) => {
  const bootstrapToken = useMemo(readBootstrapToken, []);
  const [available, setAvailable] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [registrationStatus, setRegistrationStatus] = useState(null);
  const [draft, setDraft] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    confirm_password: "",
  });
  const [pending, setPending] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [sessionReady, setSessionReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const initializeBootstrap = async () => {
      try {
        if (bootstrapToken) {
          await establishBootstrapAdminSession(bootstrapToken);
          if (cancelled) return;
          setSessionReady(true);
          window.history.replaceState({}, document.title, window.location.pathname);
        }

        const result = await getBootstrapAdminStatus();
        if (cancelled) return;
        setAvailable(Boolean(result?.available));
        setCompleted(Boolean(result?.completed));
        setSessionReady((current) => current || Boolean(result?.session_ready));
      } catch (requestError) {
        if (cancelled) return;
        setAvailable(false);
        setSessionReady(false);
        setError(requestError?.message || "Der sichere Admin-Setup-Link konnte nicht aktiviert werden.");
      }
    };

    void initializeBootstrap();
    return () => { cancelled = true; };
  }, [bootstrapToken]);

  useEffect(() => {
    let cancelled = false;

    getRegistrationStatus()
      .then((result) => {
        if (!cancelled) setRegistrationStatus(result);
      })
      .catch(() => {
        if (!cancelled) setRegistrationStatus(null);
      });

    return () => { cancelled = true; };
  }, []);

  const refreshReadiness = async () => {
    setRefreshing(true);
    setError("");
    try {
      const bootstrapStatus = await getBootstrapAdminStatus();
      setAvailable(Boolean(bootstrapStatus?.available));
      setCompleted(Boolean(bootstrapStatus?.completed));
      setSessionReady(Boolean(bootstrapStatus?.session_ready));
      try {
        setRegistrationStatus(await getRegistrationStatus());
      } catch {
        setRegistrationStatus(null);
      }
    } catch (requestError) {
      setError(requestError?.message || "AION Setup-Status konnte nicht aktualisiert werden.");
    } finally {
      setRefreshing(false);
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (!bootstrapToken && !sessionReady) {
      setError("Der sichere Admin-Setup-Link ist nicht aktiv. Bitte öffne den einmaligen Setup-Link erneut.");
      return;
    }
    if (draft.password !== draft.confirm_password) {
      setError("Passwörter stimmen nicht überein.");
      return;
    }
    setPending(true);
    try {
      const result = await bootstrapPlatformAdmin({
        ...(bootstrapToken ? { bootstrap_token: bootstrapToken } : {}),
        email: draft.email.trim(),
        first_name: draft.first_name.trim(),
        last_name: draft.last_name.trim(),
        password: draft.password,
      });
      await onAuthenticated(result);
      // App.handleAuthenticated moves the URL to /app and renders the authenticated
      // dashboard in-place. Avoid a second hard navigation here: Safari can briefly
      // restore the pre-auth document while the new HttpOnly session is settling.
    } catch (requestError) {
      try {
        const status = await getBootstrapAdminStatus();
        if (status?.completed) {
          setError("Der Erst-Admin wurde erstellt. Du wirst zur Anmeldung weitergeleitet.");
          window.setTimeout(() => { window.location.href = "/login"; }, 700);
          return;
        }
      } catch {
        // Preserve the original request error if the follow-up status check fails.
      }
      setError(requestError?.message || "Erst-Admin konnte nicht erstellt werden.");
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="dashboard-shell" data-testid="bootstrap-admin-screen">
      <div className="dashboard-background" />
      <Card className="login-card auth-card">
        <CardHeader>
          <div className="login-logo-stack">
            <Badge variant="outline" className="w-fit">AION · FIRST ADMIN</Badge>
            <AionLogoArtwork variant="main" className="aion-auth-brand" imageClassName="aion-auth-brand-image" />
          </div>
          <CardTitle className="login-title">AION Erst-Admin einrichten</CardTitle>
          <CardDescription>
            Einmalige sichere Einrichtung des ersten PLATFORM_ADMIN. Nach Erfolg wird dieser Setup-Weg automatisch deaktiviert.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="aion-compact-status-grid" data-testid="bootstrap-admin-readiness-grid">
            <div className="aion-compact-status-box">
              <span>Erst-Admin</span>
              <strong>{completed ? "ANGELEGT" : available === true ? "BEREIT" : available === false ? "NICHT BEREIT" : "PRÜFUNG"}</strong>
            </div>
            <div className="aion-compact-status-box">
              <span>Setup-Sitzung</span>
              <strong>{sessionReady ? "AKTIV" : available === true ? "SICHERER LINK NÖTIG" : "—"}</strong>
            </div>
            <div className="aion-compact-status-box">
              <span>Registrierung</span>
              <strong>{registrationStatus == null ? "PRÜFUNG" : registrationStatus?.registration_enabled ? "FREIGESCHALTET" : "GESPERRT"}</strong>
            </div>
          </div>
          {available === false ? <div className="aion-note">Der Erst-Admin-Setup ist nicht mehr verfügbar oder wurde bereits abgeschlossen.</div> : null}
          {available === true && !bootstrapToken && !sessionReady ? (
            <div className="aion-note" data-testid="bootstrap-admin-link-required">
              Der sichere Einmal-Link ist in diesem Tab nicht mehr aktiv. Öffne den Admin-Setup-Link erneut; danach bleibt die Sitzung für 30 Minuten sicher im Browser aktiv.
            </div>
          ) : null}
          {sessionReady ? <div className="aion-note" data-testid="bootstrap-admin-session-ready">Sicherer Admin-Setup ist aktiviert.</div> : null}
          {registrationStatus?.registration_enabled === false ? (
            <div className="aion-note" data-testid="bootstrap-registration-next-step">
              Nach dem Erst-Admin bleibt die Kundenregistrierung gesperrt, bis SMTP unter Admin → AI & VOICE → E-Mail & Registrierung vollständig eingerichtet ist.
            </div>
          ) : null}
          {registrationStatus?.registration_enabled === true ? (
            <div className="aion-note" data-testid="bootstrap-registration-ready">
              E-Mail-Verifizierung ist serverseitig bereit; die öffentliche Registrierung kann sicher arbeiten.
            </div>
          ) : null}
          <div className="aion-inline-actions" data-testid="bootstrap-admin-readiness-actions">
            <Button type="button" variant="outline" onClick={refreshReadiness} disabled={pending || refreshing} data-testid="bootstrap-admin-refresh-status">
              {refreshing ? "Status wird geprüft …" : "Status neu prüfen"}
            </Button>
            {completed ? (
              <Button type="button" onClick={() => { window.location.href = "/login"; }} data-testid="bootstrap-admin-login-button">
                Zur Anmeldung
              </Button>
            ) : null}
          </div>
          {error ? <div className="error-box">{error}</div> : null}
          <form className="login-form" onSubmit={submit}>
            <div className="auth-two-column-grid">
              <label className="login-field"><span>Vorname</span><Input value={draft.first_name} onChange={(event) => setDraft((prev) => ({ ...prev, first_name: event.target.value }))} required /></label>
              <label className="login-field"><span>Nachname</span><Input value={draft.last_name} onChange={(event) => setDraft((prev) => ({ ...prev, last_name: event.target.value }))} required /></label>
            </div>
            <label className="login-field"><span>E-Mail</span><Input type="email" value={draft.email} onChange={(event) => setDraft((prev) => ({ ...prev, email: event.target.value }))} required /></label>
            <label className="login-field"><span>Starkes Passwort</span><Input type="password" autoComplete="new-password" value={draft.password} onChange={(event) => setDraft((prev) => ({ ...prev, password: event.target.value }))} required /></label>
            <label className="login-field"><span>Passwort bestätigen</span><Input type="password" autoComplete="new-password" value={draft.confirm_password} onChange={(event) => setDraft((prev) => ({ ...prev, confirm_password: event.target.value }))} required /></label>
            <div className="aion-note">Mindestens 12 Zeichen, Groß-/Kleinbuchstaben, Zahl und Sonderzeichen.</div>
            <Button type="submit" className="login-button" disabled={pending || available !== true || (!bootstrapToken && !sessionReady)}>
              {pending ? "Admin wird erstellt …" : "Erst-Admin sicher erstellen"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
